import { useEffect, useState } from "react";

export interface ServiceNotice {
    enabled: boolean;
    severity: "info" | "warning" | "critical";
    message: string;
}

const SERVICE_NOTICE_POLL_MS = 60 * 1000;
const SERVICE_NOTICE_TIMEOUT_MS = 5 * 1000;

// Outlives the component: every dashboard page mounts its own shell, so
// without it the banner would vanish and reappear on each navigation.
let lastNotice: ServiceNotice | null = null;

/** Admin service notice. Public endpoint, so it works signed out and while sign-in is down. */
export function useServiceNotice(): ServiceNotice | null {
    const [notice, setNotice] = useState<ServiceNotice | null>(lastNotice);

    useEffect(() => {
        let controller: AbortController | null = null;
        const load = () => {
            controller?.abort();
            const current = new AbortController();
            controller = current;
            const timer = setTimeout(() => current.abort(), SERVICE_NOTICE_TIMEOUT_MS);
            fetch("/api/service-notice", {
                cache: "no-store",
                signal: current.signal,
            })
                .then((response) => (response.ok ? response.json() : Promise.reject(response.status)))
                .then((body: { serviceNotice?: ServiceNotice | null }) => {
                    lastNotice = body.serviceNotice ?? null;
                    setNotice(lastNotice);
                })
                // Keep the last notice shown when a poll fails.
                .catch(() => {})
                .finally(() => clearTimeout(timer));
        };
        load();
        const interval = setInterval(load, SERVICE_NOTICE_POLL_MS);
        window.addEventListener("focus", load);
        return () => {
            clearInterval(interval);
            window.removeEventListener("focus", load);
            controller?.abort();
        };
    }, []);

    return notice;
}
